import { api, apiRequest, downloadFile, getStoredAccessToken, persistSession } from "./client";
import type {
  AcordFormCatalogResponse,
  AcordSubmissionResponse,
  ClaimDetail,
  ClaimDocument,
  ClaimDocumentListResponse,
  ClaimFinancialSummary,
  ClaimListResponse,
  ClaimNote,
  ClaimNoteListResponse,
  Client360Response,
  ClientSummarizeResponse,
  CommandCenterResponse,
  CopilotChatResponse,
  CrmDashboardResponse,
  AgencyCommandCenterResponse,
  CrmReportsSummary,
  ClaimsAnalyticsResponse,
  AdjusterPerformanceReport,
  ClaimTimelineResponse,
  AgencyUserListResponse,
  AgencyUserInviteResponse,
  AgencyUserSummary,
  AgencyListResponse,
  CarrierListResponse,
  LineOfBusinessListResponse,
  SubmissionDetail,
  SubmissionListResponse,
  SubmissionSendResponse,
  PolicyPaymentTracker,
  CustomerPaymentTrackersResponse,
  PaymentActionResponse,
  AutomationRule,
  AutomationRun,
  AutomationRunNowResponse,
  FraudRuleConfig,
  AiScorecardResponse,
  CeleryFailedTask,
  FraudModelMetrics,
  MfaSetupResponse,
  CustomerListResponse,
  CustomerProfile,
  Policy,
  AccountListResponse,
  Invoice,
  InvoiceListResponse,
  Receipt,
  CarrierPayableListResponse,
  TrialBalanceResponse,
  AgingResponse,
  QboStatus,
  QboConnectResponse,
  QboSyncResponse,
  QboSyncLogResponse,
  SavedView,
  SavedViewListResponse,
  CommissionSplits,
  CustomFieldDef,
  CustomFieldDefList,
  CustomFieldValues,
  Exposure,
  ExposureList,
  CoverageGaps,
  CustomerDocumentRecord,
  CustomerNoteRecord,
  CustomerTaskRecord,
  EscalationTargetsResponse,
  Lead,
  LeadListResponse,
  PolicyEndorsement,
  MyBookResponse,
  ProducerAssistResponse,
  ProducerPriorityListResponse,
  ProducerCommissionSummary,
  ProducerCommissionStatement,
  CarrierCommissionStatementDetail,
  CarrierCommissionStatementList,
  CommissionExceptionList,
  CommissionLineMatch,
  ReconciliationKpis,
  PolicyDetail,
  PolicyListResponse,
  RenewalWorkbenchResponse,
  BatchRenewalTasksResponse,
  BatchRenewalOutreachResponse,
  RescoreResponse,
  ReviewAssignment,
  FraudExplainabilityResponse,
  SimilarClaimsResponse,
  UnifiedDocumentListResponse,
  InboundDocumentItem,
  InboundDocumentListResponse,
  InboundRoutingKpis,
  MigrationCatalogResponse,
  ImportResultResponse,
  ImportBatchListResponse,
  ImportBatchDetail,
  ImportBatchRowResponse,
  IntegrationStatus,
  QuoteBindResponse,
  QuoteListResponse,
  CarrierRaterListResponse,
  CarrierQuoteOption,
  MultiCarrierQuoteResponse,
  ProgressiveRaterStatus,
  ProgressiveRaterTestResult,
  RaterQuoteRequest,
  RateShopContext,
  SaveRaterQuoteResponse,
  GlobalSearchResult,
  UnlinkedClaimListResponse,
  BillingStatus,
  NotificationItem,
  NotificationListResponse,
  NotificationSummary,
  TokenResponse,
  UserProfile,
  WorkItemListResponse,
} from "./types";

export async function login(
  email: string,
  password: string,
  mfaCode?: string,
): Promise<TokenResponse> {
  const data = await apiRequest<TokenResponse>("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, mfa_code: mfaCode || undefined }),
    skipAuth: true,
  });
  persistSession(data.access_token, data.user);
  return data;
}

export async function setupMfaWithToken(enrollmentToken: string): Promise<{ secret: string; provisioning_uri: string }> {
  return apiRequest("/v1/auth/mfa/setup", {
    method: "POST",
    headers: { Authorization: `Bearer ${enrollmentToken}` },
    skipAuth: true,
  });
}

export async function confirmMfaWithToken(enrollmentToken: string, code: string): Promise<void> {
  await apiRequest("/v1/auth/mfa/confirm", {
    method: "POST",
    headers: { Authorization: `Bearer ${enrollmentToken}` },
    body: JSON.stringify({ code }),
    skipAuth: true,
  });
}

export async function listOidcProviders(): Promise<{
  providers: Array<{ key: string; label: string; enabled: boolean }>;
  frontend_redirect: string;
}> {
  return api.get("/v1/auth/oidc/providers");
}

export async function startOidc(provider: string): Promise<{ authorize_url: string }> {
  return api.get(`/v1/auth/oidc/${provider}/start`);
}

export async function completeOidcMfa(
  oidcMfaToken: string,
  mfaCode: string,
): Promise<TokenResponse> {
  const data = await apiRequest<TokenResponse>("/v1/auth/oidc/mfa", {
    method: "POST",
    body: JSON.stringify({ oidc_mfa_token: oidcMfaToken, mfa_code: mfaCode }),
    skipAuth: true,
  });
  persistSession(data.access_token, data.user);
  return data;
}

export async function fetchMe(): Promise<UserProfile> {
  return api.get<UserProfile>("/v1/auth/me");
}

export async function logout(): Promise<void> {
  // Refresh token is sent via the httpOnly cookie; server revokes + clears it.
  await api.post<void>("/v1/auth/logout", {});
}

export const dashboardApi = {
  crm: () => api.get<CrmDashboardResponse>("/v1/crm/dashboard"),
  commandCenter: () => api.get<CommandCenterResponse>("/v1/command-center"),
  agencyCommandCenter: () => api.get<AgencyCommandCenterResponse>("/v1/agency/command-center"),
};

export type ClientQuery = {
  q?: string;
  status?: string;
  sort?: string;
  hasBalance?: boolean;
  assignedProducerId?: string;
  limit?: number;
};

export const clientsApi = {
  list: (q?: string) =>
    api.get<CustomerListResponse>(`/v1/crm/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  query: (params: ClientQuery = {}) => {
    const p = new URLSearchParams();
    if (params.q) p.set("q", params.q);
    if (params.status) p.set("status", params.status);
    if (params.sort) p.set("sort", params.sort);
    if (params.hasBalance !== undefined) p.set("has_balance", String(params.hasBalance));
    if (params.assignedProducerId) p.set("assigned_producer_id", params.assignedProducerId);
    if (params.limit) p.set("limit", String(params.limit));
    const s = p.toString();
    return api.get<CustomerListResponse>(`/v1/crm/customers${s ? `?${s}` : ""}`);
  },
  get360: (customerId: string) => api.get<Client360Response>(`/v1/clients/${customerId}/360`),
  summarize: (customerId: string) =>
    api.post<ClientSummarizeResponse>(`/v1/clients/${customerId}/copilot/summarize`, {}),
  create: (payload: {
    first_name?: string;
    last_name?: string;
    company_name?: string;
    entity_type?: string;
    date_of_birth?: string;
    email?: string;
    phone?: string;
    lead_source?: string;
    customer_status?: string;
    address?: Record<string, string>;
  }) => api.post<CustomerProfile>("/v1/crm/customers", payload),
};

export const workApi = {
  inbox: () => api.get<WorkItemListResponse>("/v1/work-items/inbox?mine=true&open_only=true"),
  myBook: () => api.get<MyBookResponse>("/v1/crm/my-book"),
  producerPriorities: () => api.get<ProducerPriorityListResponse>("/v1/work/producer-priorities"),
  producerAssist: (
    customerId: string,
    payload: { assist_type: "email" | "call_script"; reason_codes: string[] },
  ) =>
    api.post<ProducerAssistResponse>(`/v1/work/producer-priorities/${customerId}/assist`, payload),
  completeItem: (scope: "client" | "claim", itemId: string) =>
    api.patch(`/v1/work-items/${scope}/${itemId}`, { status: "completed" }),
};

export const dataQualityApi = {
  unlinkedClaims: (limit = 5) =>
    api.get<UnlinkedClaimListResponse>(`/v1/data-quality/unlinked-claims?limit=${limit}`),
  linkClaim: (claimId: string) =>
    api.post<{ id: string; customer_id: string | null }>(`/v1/data-quality/unlinked-claims/${claimId}/link`, {}),
};

export const enterpriseApi = {
  agencyUsers: (params?: { role?: string; activeOnly?: boolean }) => {
    const query = new URLSearchParams();
    if (params?.role) query.set("role", params.role);
    if (params?.activeOnly === false) query.set("active_only", "false");
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return api.get<AgencyUserListResponse>(`/v1/enterprise/agency-users${suffix}`);
  },
  inviteUser: (payload: { email: string; display_name: string; role: string }) =>
    api.post<AgencyUserInviteResponse>("/v1/enterprise/agency-users", payload),
  updateUser: (userId: string, payload: { role?: string; active?: boolean }) =>
    api.patch<AgencyUserSummary & { active: boolean; mfa_enabled: boolean }>(
      `/v1/enterprise/agency-users/${userId}`,
      payload,
    ),
  agencies: (activeOnly = true) =>
    api.get<AgencyListResponse>(`/v1/enterprise/agencies?active_only=${activeOnly}`),
  carriers: (activeOnly = true) =>
    api.get<CarrierListResponse>(`/v1/enterprise/carriers?active_only=${activeOnly}`),
  linesOfBusiness: (activeOnly = true) =>
    api.get<LineOfBusinessListResponse>(`/v1/enterprise/lines-of-business?active_only=${activeOnly}`),
};

export const adminApi = {
  automationRules: () => api.get<AutomationRule[]>("/v1/automation/rules"),
  updateAutomationRule: (ruleKey: string, payload: { enabled?: boolean; config?: Record<string, unknown> }) =>
    api.patch<AutomationRule>(`/v1/automation/rules/${ruleKey}`, payload),
  automationRuns: () => api.get<AutomationRun[]>("/v1/automation/runs"),
  runAutomationNow: () => api.post<AutomationRunNowResponse>("/v1/automation/run-now", {}),
  fraudRules: () => api.get<FraudRuleConfig[]>("/v1/fraud/rules"),
  updateFraudRule: (ruleKey: string, payload: { value: number; description?: string }) =>
    api.put<FraudRuleConfig>(`/v1/fraud/rules/${ruleKey}`, payload),
  aiScorecard: () => api.get<AiScorecardResponse>("/v1/ops/ai/scorecard"),
  failedJobs: (includeResolved = false) =>
    api.get<CeleryFailedTask[]>(`/v1/ops/jobs/failed?include_resolved=${includeResolved}`),
  resolveFailedJob: (taskRowId: string) =>
    api.post<{ status: string; id: string }>(`/v1/ops/jobs/failed/${taskRowId}/resolve`, {}),
  fraudModelMetrics: () => api.get<FraudModelMetrics>("/v1/ml/metrics"),
};

export const aiApi = {
  submitFeedback: (payload: {
    feature: string;
    target_type: string;
    target_id: string;
    rating: "thumbs_up" | "thumbs_down";
    override_reason?: string;
    context?: Record<string, unknown>;
  }) => api.post<{ id: string; feature: string; rating: string; created_at: string }>("/v1/ai/feedback", payload),
};

export const authApi = {
  mfaSetup: () => api.post<MfaSetupResponse>("/v1/auth/mfa/setup", {}),
  mfaConfirm: (code: string) => api.post<{ message: string }>("/v1/auth/mfa/confirm", { code }),
};

export const crmApi = {
  createNote: (customerId: string, body: string, pinned = false) =>
    api.post<CustomerNoteRecord>(`/v1/crm/customers/${customerId}/notes`, { body, pinned }),
  createTask: (
    customerId: string,
    payload: {
      title: string;
      description?: string;
      due_at?: string;
      assignee_id?: string;
    },
  ) => api.post<CustomerTaskRecord>(`/v1/crm/customers/${customerId}/tasks`, payload),
  completeTask: (customerId: string, taskId: string) =>
    api.patch<CustomerTaskRecord>(`/v1/crm/customers/${customerId}/tasks/${taskId}`, { status: "completed" }),
  uploadDocument: (customerId: string, formData: FormData) =>
    apiRequest<CustomerDocumentRecord>(`/v1/crm/customers/${customerId}/documents`, {
      method: "POST",
      body: formData,
    }),
};

export const claimsApi = {
  list: (limit = 50) => api.get<ClaimListResponse>(`/v1/claims/summary?limit=${limit}`),
  get: (claimId: string) => api.get<ClaimDetail>(`/v1/claims/${claimId}`),
  createFnol: (payload: {
    policy_id: string;
    claim_type: string;
    amount_cents: number;
    incident_date: string;
    description?: string;
    injury_involved?: boolean;
    cause_of_loss?: string;
    reported_date?: string;
    reporter_name?: string;
    reporter_phone?: string;
    loss_location?: Record<string, string>;
  }) => api.post<ClaimDetail>("/v1/claims/agent", payload),
  timeline: (claimId: string) => api.get<ClaimTimelineResponse>(`/v1/claims/${claimId}/timeline`),
  commandCenter: () => api.get<CommandCenterResponse>("/v1/command-center"),
  copilotChat: (claimId: string, message: string) =>
    api.post<CopilotChatResponse>(`/v1/claims/${claimId}/copilot/chat`, {
      message,
      provider: "claude",
    }),
  listDocuments: (claimId: string) => api.get<ClaimDocumentListResponse>(`/v1/claims/${claimId}/documents`),
  uploadDocument: (claimId: string, formData: FormData) =>
    apiRequest<ClaimDocument>(`/v1/claims/${claimId}/documents`, { method: "POST", body: formData }),
  listNotes: (claimId: string) => api.get<ClaimNoteListResponse>(`/v1/claims/${claimId}/notes`),
  addNote: (claimId: string, body: string, pinned = false) =>
    api.post<ClaimNote>(`/v1/claims/${claimId}/notes`, { body, pinned }),
  financial: (claimId: string) => api.get<ClaimFinancialSummary>(`/v1/claims/${claimId}/financial`),
  addReserve: (claimId: string, amount_cents: number, note?: string) =>
    api.post(`/v1/claims/${claimId}/reserves`, { amount_cents, note }),
  addPayment: (claimId: string, payee_name: string, amount_cents: number, notes?: string) =>
    api.post(`/v1/claims/${claimId}/payments`, { payee_name, amount_cents, notes }),
  addCoverageDecision: (claimId: string, outcome: string, rationale: string) =>
    api.post(`/v1/claims/${claimId}/coverage-decisions`, { outcome, rationale }),
  rescore: (claimId: string) =>
    api.post<RescoreResponse>(`/v1/claims/${claimId}/rescore?refresh_enrichment=true`, {}),
  fraudExplainability: (claimId: string) =>
    api.get<FraudExplainabilityResponse>(`/v1/claims/${claimId}/fraud/explainability`),
  similarClaims: (claimId: string) =>
    api.get<SimilarClaimsResponse>(`/v1/claims/${claimId}/similar`),
};

export const reviewApi = {
  escalationTargets: () => api.get<EscalationTargetsResponse>("/v1/review/escalation-targets"),
  submitDecision: (
    assignmentId: string,
    payload: {
      decision: "approve" | "deny" | "request_info" | "escalate";
      notes?: string;
      escalate_to_reviewer_id?: string;
      escalation_lane?: "supervisor" | "siu" | "legal";
    },
  ) => api.patch<ReviewAssignment>(`/v1/review/assignments/${assignmentId}`, payload),
};

export const pipelineApi = {
  leads: (lane: "sales" | "renewal" = "sales") =>
    api.get<LeadListResponse>(`/v1/crm/leads?pipeline_lane=${lane}&limit=200`),
  updateLead: (leadId: string, payload: { stage?: string }) =>
    api.patch(`/v1/crm/leads/${leadId}`, payload),
  createLead: (payload: {
    contact_name: string;
    contact_email?: string;
    company_name?: string;
    product_line: string;
    estimated_premium_cents?: number;
    stage?: string;
    description?: string;
    assigned_to_id?: string;
    pipeline_lane?: "sales" | "renewal";
  }) => api.post<Lead>("/v1/crm/leads", payload),
};

export const quotesApi = {
  list: (status?: string) =>
    api.get<QuoteListResponse>(`/v1/crm/quotes${status && status !== "all" ? `?status=${status}` : ""}`),
  bind: (quoteId: string) => api.post<QuoteBindResponse>(`/v1/crm/quotes/${quoteId}/bind`, {}),
};

export const policiesApi = {
  list: (q?: string, customerId?: string) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (customerId) params.set("customer_id", customerId);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return api.get<PolicyListResponse>(`/v1/policies${suffix}`);
  },
  detail: (policyId: string) => api.get<PolicyDetail>(`/v1/crm/policies/${policyId}/detail`),
  commissionSplits: (policyId: string) =>
    api.get<CommissionSplits>(`/v1/crm/policies/${policyId}/commission-splits`),
  setCommissionSplits: (
    policyId: string,
    splits: Array<{ producer_id: string; role: string; split_percent: number }>,
  ) => api.put<CommissionSplits>(`/v1/crm/policies/${policyId}/commission-splits`, { splits }),
  query: (params: {
    q?: string;
    customerId?: string;
    status?: string;
    sort?: string;
    expirationFrom?: string;
    expirationTo?: string;
    premiumMin?: number;
    premiumMax?: number;
    limit?: number;
  } = {}) => {
    const p = new URLSearchParams();
    if (params.q) p.set("q", params.q);
    if (params.customerId) p.set("customer_id", params.customerId);
    if (params.status) p.set("status", params.status);
    if (params.sort) p.set("sort", params.sort);
    if (params.expirationFrom) p.set("expiration_from", params.expirationFrom);
    if (params.expirationTo) p.set("expiration_to", params.expirationTo);
    if (params.premiumMin !== undefined) p.set("premium_min", String(params.premiumMin));
    if (params.premiumMax !== undefined) p.set("premium_max", String(params.premiumMax));
    if (params.limit) p.set("limit", String(params.limit));
    const s = p.toString();
    return api.get<PolicyListResponse>(`/v1/policies${s ? `?${s}` : ""}`);
  },
  create: (payload: {
    customer_id: string;
    product_line?: string;
    policy_number?: string;
    carrier_id?: string;
    lob_id?: string;
    status?: string;
    effective_date?: string;
    expiration_date?: string;
    premium_cents?: number;
    coverage_limit_cents?: number;
    commission_percent?: number;
  }) => api.post<Policy>("/v1/crm/policies", payload),
  addEndorsement: (
    policyId: string,
    payload: {
      endorsement_type: string;
      description: string;
      effective_date: string;
      premium_change_cents?: number;
    },
  ) => api.post<PolicyEndorsement>(`/v1/crm/policies/${policyId}/endorsements`, payload),
  renewalsWorkbench: (scoped = true) =>
    api.get<RenewalWorkbenchResponse>(`/v1/crm/renewals/workbench?scoped=${scoped}`),
  batchRenewalTasks: (policyIds: string[], taskMode: "auto" | "renewal" | "remarket" = "auto") =>
    api.post<BatchRenewalTasksResponse>("/v1/crm/renewals/batch/tasks", {
      policy_ids: policyIds,
      task_mode: taskMode,
    }),
  remarketTask: (policyId: string) =>
    api.post<{ task_id: string; policy_id: string; message: string }>(
      `/v1/crm/renewals/${policyId}/remarket-task`,
      {},
    ),
  batchRenewalOutreach: (
    policyIds: string[],
    assistType: "email" | "call_script" = "email",
  ) =>
    api.post<BatchRenewalOutreachResponse>("/v1/crm/renewals/batch/outreach", {
      policy_ids: policyIds,
      assist_type: assistType,
    }),
};

export const accountingApi = {
  accounts: () => api.get<AccountListResponse>("/v1/accounting/accounts"),
  invoices: (params?: { status?: string; customerId?: string }) => {
    const q = new URLSearchParams();
    if (params?.status && params.status !== "all") q.set("status", params.status);
    if (params?.customerId) q.set("customer_id", params.customerId);
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return api.get<InvoiceListResponse>(`/v1/accounting/invoices${suffix}`);
  },
  invoice: (id: string) => api.get<Invoice>(`/v1/accounting/invoices/${id}`),
  createInvoice: (payload: {
    customer_id: string;
    policy_id?: string;
    bill_type?: string;
    due_date?: string;
    memo?: string;
    lines: Array<{ kind?: string; description: string; amount_cents: number; commission_percent?: number }>;
  }) => api.post<Invoice>("/v1/accounting/invoices", payload),
  recordReceipt: (payload: {
    customer_id: string;
    method?: string;
    amount_cents: number;
    received_date?: string;
    reference?: string;
    applications: Array<{ invoice_id: string; amount_cents: number }>;
  }) => api.post<Receipt>("/v1/accounting/receipts", payload),
  carrierPayables: (status?: string) => {
    const suffix = status && status !== "all" ? `?status=${status}` : "";
    return api.get<CarrierPayableListResponse>(`/v1/accounting/carrier-payables${suffix}`);
  },
  trialBalance: () => api.get<TrialBalanceResponse>("/v1/accounting/reports/trial-balance"),
  aging: () => api.get<AgingResponse>("/v1/accounting/reports/aging"),
  exportCsv: (entity: string) => downloadFile(`/v1/accounting/export?entity=${entity}`, `${entity}.csv`),
  invoicePdf: (invoiceId: string, invoiceNumber: string) =>
    downloadFile(`/v1/accounting/invoices/${invoiceId}/pdf`, `${invoiceNumber}.pdf`),
  statementPdf: (customerId: string) =>
    downloadFile(`/v1/accounting/customers/${customerId}/statement`, `statement-${customerId.slice(0, 8)}.pdf`),
  paymentTracker: (policyId: string) =>
    api.get<PolicyPaymentTracker>(`/v1/accounting/policies/${policyId}/payment-tracker`),
  customerPaymentTrackers: (customerId: string) =>
    api.get<CustomerPaymentTrackersResponse>(`/v1/accounting/customers/${customerId}/payment-trackers`),
  sendPaymentReminder: (policyId: string) =>
    api.post<PaymentActionResponse>(`/v1/accounting/policies/${policyId}/payment-reminder`, {}),
  sendInvoiceEmail: (invoiceId: string) =>
    api.post<PaymentActionResponse>(`/v1/accounting/invoices/${invoiceId}/send`, {}),
};

export const quickbooksApi = {
  status: () => api.get<QboStatus>("/v1/integrations/quickbooks/status"),
  connect: () => api.get<QboConnectResponse>("/v1/integrations/quickbooks/connect"),
  disconnect: () => api.post<QboStatus>("/v1/integrations/quickbooks/disconnect", {}),
  sync: () => api.post<QboSyncResponse>("/v1/integrations/quickbooks/sync", {}),
  syncLog: (limit = 50) => api.get<QboSyncLogResponse>(`/v1/integrations/quickbooks/sync/log?limit=${limit}`),
  iif: () => downloadFile("/v1/integrations/quickbooks/iif", "agency_ledger.iif"),
};

export const viewsApi = {
  list: (entity: string) => api.get<SavedViewListResponse>(`/v1/views?entity=${entity}`),
  create: (payload: { entity: string; name: string; filters: Record<string, string>; sort?: string | null }) =>
    api.post<SavedView>("/v1/views", payload),
  remove: (id: string) => api.del<void>(`/v1/views/${id}`),
};

export const customFieldsApi = {
  defs: (entity?: string) =>
    api.get<CustomFieldDefList>(`/v1/custom-fields/defs${entity ? `?entity=${entity}` : ""}`),
  createDef: (payload: {
    entity: string;
    field_key: string;
    label: string;
    data_type: string;
    options?: string[];
    required?: boolean;
  }) => api.post<CustomFieldDef>("/v1/custom-fields/defs", payload),
  deleteDef: (id: string) => api.del<void>(`/v1/custom-fields/defs/${id}`),
  values: (entity: string, entityId: string) =>
    api.get<CustomFieldValues>(`/v1/custom-fields/${entity}/${entityId}`),
  setValues: (entity: string, entityId: string, values: Record<string, unknown>) =>
    api.put<CustomFieldValues>(`/v1/custom-fields/${entity}/${entityId}`, { values }),
};

export const exposuresApi = {
  list: (customerId: string) => api.get<ExposureList>(`/v1/crm/customers/${customerId}/exposures`),
  add: (
    customerId: string,
    payload: { kind: string; label: string; count: number; value_cents?: number; state?: string },
  ) => api.post<Exposure>(`/v1/crm/customers/${customerId}/exposures`, payload),
  remove: (customerId: string, id: string) =>
    api.del<void>(`/v1/crm/customers/${customerId}/exposures/${id}`),
  coverageGaps: (customerId: string) =>
    api.get<CoverageGaps>(`/v1/crm/customers/${customerId}/coverage-gaps`),
};

export const documentsApi = {
  library: (params: { q?: string; scope?: string; customerId?: string }) => {
    const query = new URLSearchParams();
    if (params.q) query.set("q", params.q);
    if (params.scope) query.set("scope", params.scope);
    if (params.customerId) query.set("customer_id", params.customerId);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return api.get<UnifiedDocumentListResponse>(`/v1/documents/library${suffix}`);
  },
  inboundKpis: () => api.get<InboundRoutingKpis>("/v1/inbound/kpis"),
  listInbound: (routingStatus?: string) => {
    const query = routingStatus ? `?routing_status=${encodeURIComponent(routingStatus)}` : "";
    return api.get<InboundDocumentListResponse>(`/v1/inbound/documents${query}`);
  },
  uploadInbound: (formData: FormData) =>
    apiRequest<InboundDocumentItem>("/v1/inbound/documents/upload", {
      method: "POST",
      body: formData,
    }),
  ingestEmail: (payload: {
    from_address: string;
    subject: string;
    body?: string;
    filename?: string;
  }) => api.post<InboundDocumentItem>("/v1/inbound/email", payload),
  routeInbound: (
    inboundId: string,
    payload: { customer_id: string; policy_id?: string; claim_id?: string; notes?: string },
  ) => api.post<InboundDocumentItem>(`/v1/inbound/documents/${inboundId}/route`, payload),
  rejectInbound: (inboundId: string, notes?: string) =>
    api.post<InboundDocumentItem>(`/v1/inbound/documents/${inboundId}/reject`, { notes }),
};

export const settingsApi = {
  integrations: () => api.get<IntegrationStatus>("/v1/integrations/status"),
};

export const dataMigrationApi = {
  catalog: () => api.get<MigrationCatalogResponse>("/v1/data-migration/catalog"),
  exportCsv: async (entity: string, format: string) => {
    const token = getStoredAccessToken();
    const base = import.meta.env.VITE_API_BASE_URL ?? "";
    const response = await fetch(
      `${base}/v1/data-migration/export?entity=${encodeURIComponent(entity)}&format=${encodeURIComponent(format)}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    if (!response.ok) {
      let detail = `Export failed (${response.status})`;
      try {
        const body = await response.json();
        detail = body.detail || detail;
      } catch {
        /* keep default */
      }
      throw new Error(detail);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    return { blob, filename: match?.[1] || `${entity}_${format}.csv` };
  },
  importCsv: (formData: FormData) =>
    apiRequest<ImportResultResponse>("/v1/data-migration/import", {
      method: "POST",
      body: formData,
    }),
  listBatches: () => api.get<ImportBatchListResponse>("/v1/data-migration/batches"),
  getBatch: (batchId: string) =>
    api.get<ImportBatchDetail>(`/v1/data-migration/batches/${batchId}`),
  rollbackBatch: (batchId: string) =>
    api.post<ImportBatchDetail>(`/v1/data-migration/batches/${batchId}/rollback`, {}),
  listAmbiguous: (batchId?: string) => {
    const q = batchId ? `?batch_id=${batchId}` : "";
    return api.get<ImportBatchRowResponse[]>(`/v1/data-migration/ambiguous${q}`);
  },
  resolveAmbiguous: (rowId: string, payload: { customer_id?: string; force_create?: boolean }) =>
    api.post<ImportBatchRowResponse>(`/v1/data-migration/ambiguous/${rowId}/resolve`, payload),
};

export const ratersApi = {
  context: (params: { leadId?: string; customerId?: string }) => {
    const query = new URLSearchParams();
    if (params.leadId) query.set("lead_id", params.leadId);
    if (params.customerId) query.set("customer_id", params.customerId);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return api.get<RateShopContext>(`/v1/raters/context${suffix}`);
  },
  listCarriers: (productLine?: string) =>
    api.get<CarrierRaterListResponse>(
      `/v1/raters/carriers${productLine ? `?product_line=${encodeURIComponent(productLine)}` : ""}`,
    ),
  quote: (payload: RaterQuoteRequest) => api.post<MultiCarrierQuoteResponse>("/v1/raters/quote", payload),
  progressiveStatus: () => api.get<ProgressiveRaterStatus>("/v1/raters/progressive/status"),
  testProgressive: () => api.post<ProgressiveRaterTestResult>("/v1/raters/progressive/test", {}),
  hartfordStatus: () => api.get<ProgressiveRaterStatus>("/v1/raters/hartford/status"),
  testHartford: () => api.post<ProgressiveRaterTestResult>("/v1/raters/hartford/test", {}),
  saveQuote: (payload: {
    request_id: string;
    option: CarrierQuoteOption;
    customer_id?: string;
    lead_id?: string;
  }) => api.post<SaveRaterQuoteResponse>("/v1/raters/quote/save", payload),
};

export const commissionsApi = {
  myStatement: () => api.get<ProducerCommissionStatement>("/v1/crm/commissions/my-statement"),
  agencySummary: () => api.get<ProducerCommissionSummary>("/v1/crm/commissions/producer-summary"),
  producerStatement: (producerId: string) =>
    api.get<ProducerCommissionStatement>(`/v1/crm/commissions/producers/${producerId}/statement`),
  reconciliationKpis: () => api.get<ReconciliationKpis>("/v1/crm/commissions/reconciliation/kpis"),
  listStatements: () => api.get<CarrierCommissionStatementList>("/v1/crm/commissions/statements"),
  getStatement: (statementId: string) =>
    api.get<CarrierCommissionStatementDetail>(`/v1/crm/commissions/statements/${statementId}`),
  uploadStatementCsv: (formData: FormData) =>
    apiRequest<CarrierCommissionStatementDetail>("/v1/crm/commissions/statements/upload", {
      method: "POST",
      body: formData,
    }),
  listExceptions: (openOnly = true) =>
    api.get<CommissionExceptionList>(`/v1/crm/commissions/exceptions?open_only=${openOnly}`),
  resolveLine: (lineId: string, notes?: string) =>
    api.patch<CommissionLineMatch>(`/v1/crm/commissions/lines/${lineId}/resolve`, { notes }),
  postStatement: (statementId: string) =>
    api.post<{ posted: number; skipped: number; total_posted_cents: number }>(
      `/v1/crm/commissions/statements/${statementId}/post`,
      {},
    ),
};

export const reportsApi = {
  summary: (days = 90) => api.get<CrmReportsSummary>(`/v1/reports/crm/summary?days=${days}`),
};

export const claimsAnalyticsApi = {
  summary: (lookbackDays = 90) =>
    api.get<ClaimsAnalyticsResponse>(`/v1/analytics/claims?lookback_days=${lookbackDays}`),
  adjusterPerformance: (lookbackDays = 90) =>
    api.get<AdjusterPerformanceReport>(`/v1/analytics/claims/adjuster-performance?lookback_days=${lookbackDays}`),
};

export const notificationsApi = {
  summary: () => api.get<NotificationSummary>("/v1/notifications/summary"),
  listUnread: (limit = 20) => api.get<NotificationListResponse>(`/v1/notifications?unread_only=true&limit=${limit}`),
  markRead: (notificationId: string) => api.post<NotificationItem>(`/v1/notifications/${notificationId}/read`, {}),
  markAllRead: () => api.post<{ marked_read: number }>("/v1/notifications/read-all", {}),
};

export const searchApi = {
  global: async (query: string): Promise<{ results: GlobalSearchResult[]; errors: string[] }> => {
    const q = encodeURIComponent(query);
    const errors: string[] = [];
    const [clients, claims, policies] = await Promise.all([
      clientsApi.list(query).catch((err) => {
        errors.push(err instanceof Error ? err.message : "Clients search failed");
        return { items: [], total: 0 };
      }),
      api.get<ClaimListResponse>(`/v1/claims/summary?q=${q}&limit=5`).catch((err) => {
        errors.push(err instanceof Error ? err.message : "Claims search failed");
        return { items: [], total: 0 };
      }),
      policiesApi.list(query).catch((err) => {
        errors.push(err instanceof Error ? err.message : "Policies search failed");
        return { items: [], total: 0 };
      }),
    ]);
    const results: GlobalSearchResult[] = [];
    for (const customer of clients.items.slice(0, 5)) {
      const name = [customer.first_name, customer.last_name].filter(Boolean).join(" ") || customer.company_name || customer.email || "Client";
      results.push({
        id: customer.id,
        label: name,
        meta: customer.email ?? undefined,
        path: `/clients/${customer.id}`,
        type: "client",
      });
    }
    for (const claim of claims.items.slice(0, 5)) {
      results.push({
        id: claim.id,
        label: claim.external_ref ?? claim.policy_number,
        meta: `${claim.claim_type} · ${claim.status}`,
        path: `/claims/${claim.id}`,
        type: "claim",
      });
    }
    for (const policy of policies.items.slice(0, 5)) {
      results.push({
        id: policy.id,
        label: policy.policy_number,
        meta: policy.product_line ?? policy.status,
        path: `/policies/${policy.id}`,
        type: "policy",
      });
    }
    return { results, errors };
  },
};

export const billingApi = {
  status: () => api.get<BillingStatus>("/v1/billing/status"),
  checkout: (payload: { plan_tier?: string; price_id?: string }) =>
    api.post<{ checkout_url: string }>("/v1/billing/checkout", payload),
  portal: () => api.post<{ portal_url: string }>("/v1/billing/portal", {}),
};

export const submissionsApi = {
  list: (filters?: { status?: string; policyId?: string; customerId?: string; carrierId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status && filters.status !== "all") params.set("status", filters.status);
    if (filters?.policyId) params.set("policy_id", filters.policyId);
    if (filters?.customerId) params.set("customer_id", filters.customerId);
    if (filters?.carrierId) params.set("carrier_id", filters.carrierId);
    const qs = params.toString();
    return api.get<SubmissionListResponse>(`/v1/submissions${qs ? `?${qs}` : ""}`);
  },
  get: (submissionId: string) => api.get<SubmissionDetail>(`/v1/submissions/${submissionId}`),
  create: (payload: {
    carrier_id: string;
    policy_id?: string;
    quote_id?: string;
    customer_id?: string;
    product_line?: string;
    acord_form?: string;
    requested_effective_date?: string;
    notes?: string;
  }) => api.post<SubmissionDetail>("/v1/submissions", payload),
  send: (submissionId: string) => api.post<SubmissionSendResponse>(`/v1/submissions/${submissionId}/send`, {}),
  transition: (
    submissionId: string,
    payload: {
      status: string;
      note?: string;
      carrier_reference?: string;
      premium_quoted_cents?: number;
      decline_reason?: string;
    },
  ) => api.post<SubmissionDetail>(`/v1/submissions/${submissionId}/transition`, payload),
};

export const acordApi = {
  listForms: () => api.get<AcordFormCatalogResponse>("/v1/acord/forms"),
  submitPolicy: (policyId: string, formId?: string) =>
    api.post<AcordSubmissionResponse>(
      `/v1/acord/policies/${policyId}/submit${formId ? `?form=${encodeURIComponent(formId)}` : ""}`,
      {},
    ),
  download: async (sourceType: "policy" | "quote" | "claim", sourceId: string, formId: string, format: "pdf" | "xml") => {
    const token = getStoredAccessToken();
    const ext = format === "pdf" ? "pdf" : "xml";
    const path =
      sourceType === "policy"
        ? `/v1/acord/policies/${sourceId}/${format}?form=${formId}`
        : sourceType === "quote"
          ? `/v1/acord/quotes/${sourceId}/${format}?form=${formId}`
          : `/v1/acord/claims/${sourceId}/pdf?form=${formId}`;
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ""}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new Error(`ACORD download failed (${response.status})`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="(.+)"/);
    const filename = match?.[1] || `ACORD-${formId}-${sourceId.slice(0, 8)}.${ext}`;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  },
};
